/**
 * 工具类型：查找具有相同值的键
 * @example
 * type T = { a: "foo", b: "bar" }
 * ValueToKeys<T, "foo"> // 返回 "a"
 */
type ValueToKeys<T, V> = {
  [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

/**
 * 工具类型：检查API URL是否重复
 * 如果发现重复的URL，会生成友好的错误提示
 */
type CheckDuplicateUrls<T> = {
  [K in keyof T]: ValueToKeys<Omit<T, K>, T[K]> extends never
    ? T[K]
    : `[API重复] URL '${T[K] & string}' 已被 ${ValueToKeys<Omit<T, K>, T[K]> &
        string} 使用`;
};

/**
 * 创建API对象，确保URL不重复
 * @param obj API对象
 * @returns 经过类型检查的API对象
 */
function createApi<T extends Record<string, string>>(
  obj: CheckDuplicateUrls<T> & Record<string, string>
): T {
  return obj as T;
}

// 定义API对象
const Api = createApi({
  /** 获取验证码 */
  GET_VERIFY_CODE: "/facade/sale/login/verify/code/query",
  /** 获取用户机构 */
  GET_USER_STORE: "/facade/sale/user/getStoreByAccount",
  /** 登录接口 */
  GET_LOGIN: "/facade/sale/login/in",
  /** 获取未提交预审单是否超时 */
  GET_PRE_TRIAL_TIMEOUT: "/facade/sale/preTrial/preTrialTimeoutReminder",
  /** 获取入库旅游产品类型 */
  GET_TICKETS_TYPES: "/console/tp/api/tickets/types",
  /** 获取进销帐报表旅游产品类型 */
  GET_PRODUCT_TYPES: "/console/tour/product/type",
  /** 获取报表供应商旅游产品类别 */
  GET_REPORT_SUPPLIER_TOUR_PRODUCT_CATEGORY:
    "/console/tour/product/queryCategoryByGrade",
  /** 获取报表结算门市存款单筛选收款方式 */
  GET_REPORT_CollectingWayList: "/facade/sale/deposit/queryCollectingWayList",
  /** 获取代理账号新增时选择机构后得到序号 */
  GET_AGENT_UserSerialNoList: "/console/user/agency/queryUserSerialNo",
  /** 获取报表供应商旅游产品供应商列表 */
  GET_REPORT_SUPPLIER_TOUR_PRODUCT_SUPPLIER_LIST: "/console/supplier/page",
  /** 获取报表包车单人门市列表 */
  GET_RECEIPT_STORE: "/forms/charter/receipt/store/list",
  /** 代理信息总代理同步信息 */
  POST_AGENCY_SYNC: "/console/agency/sync",
  /** 代理信息总代理切换信息 */
  POST_AGENCY_SWITCH: "/console/agency/switch",
  /** 价格政策代理列表 */
  POST_PRICE_POLICY_AGENCY_LIST: "/console/priceMarket/queryPageStoreAndAgency",
  /** 获取报表供应商旅游产品类型 */
  POST_REPORT_SUPPLIER_TOUR_PRODUCT_TYPE:
    "/facade/sale/tour/product/type/queryTourProductTypeList",
  /** 获取报表供应商旅游产品商品列表 */
  POST_REPORT_SUPPLIER_TOUR_PRODUCT_LIST:
    "/facade/sale/tour/product/list/queryProductByTypes",
  /** 获取运营公司列表 */
  POST_OPERATE_COMPANY_LIST: "/console/operate/company/listAll",
  /** 查询路线出发城市区域列表 */
  GET_DEPARTURE_ROUTE_CITY_AREA_LIST:
    "/console/route/getDepartureRouteCityAreaList",
  /** 门市、代理收款方式 */
  DEPOSIT_TRANSACTION_MODE_LIST: "/facade/sale/deposit/transaction/mode/list",
  /** 获取门市交易方式列表 */
  GET_STORE_TRANSACTION_MODE_LIST: "/facade/sale/store/transaction/mode/list",
  /** 获取选座班次座位详情 */
  POST_SEAT_DETAIL: "/facade/sale/line/choose/seat/detail",
  /** 消位接口 */
  POST_COMMON_TICKET_TO_OPEN: "/facade/sale/open/ticket/commonTicketToOpen",
  /** 消位预检查接口 */
  POST_COMMON_TICKET_TO_OPEN_PRE:
    "/facade/sale/open/ticket/commonTicketToOpenPre",
  /** 获取POS机列表 */
  GET_POS_LIST: "/facade/sale/posTerminal/by/account",
  /** 发送短信 */
  POST_SEND_SMS: "/console/message/sendMessage",
  /** 短信详情 */
  GET_MESSAGE_DETAIL: "/console/message/detail",
  /** 重发信息 */
  POST_MESSAGE_RESEND: "/console/message/resendRecordMessage",
  /** 获取打印收据 */
  GET_RECEIPT: "/facade/sale/tour/product/receipt/info",
  /** 获取买断数量的车票号列表 */
  GET_STOCK_TICKET_DETAILS:
    "/facade/sale/agency/account/stock/getStockTicketDetails",
  /** 旅游销售产品详情 */
  GET_TOUR_PRODUCT_DETAILS:
    "/facade/sale/tour/product/pack/product/price/stations/query",
  /** 添加班次接口 */
  POST_ADD_LINE: "/console/line/merge/addLine",
  /** 新增产品库存 */
  POST_ADD_PRODUCT_STOCK: "/console/tour/product/stock/add",
  /** 获取有效区域列表 */
  GET_VALID_AREA_LIST: "/console/line/listArea",
  /** 获取所有站点列表 */
  GET_STATION_LIST: "/console/line/station/selectStations",
  /** 获取区域下有效站点列表 */
  GET_VALID_STATION_LIST: "/console/line/station/byArea",
  /** 查询代理不可售站点列表 */
  POST_SITE_RESTRICTIONS_LIST:
    "/console/agency/sale/forbidden/station/page/list",
  /** 代理不可售站点启用禁用 */
  POST_SITE_RESTRICTIONS_STATUS:
    "/console/agency/sale/forbidden/station/enableOrDisAble",
  /** 添加/修改代理不可售站点 */
  POST_SITE_RESTRICTIONS_ADD_OR_UPDATE:
    "/console/agency/sale/forbidden/station/saveOrUpdate",
  /** 获取代理不可售详情 */
  GET_SITE_RESTRICTIONS_DETAIL: "/console/agency/sale/forbidden/station/detail",

  /** 获取门市列表 */
  GET_Stores_With_Permission: "/facade/sale/user/store/getStoresWithPermission",

  /** 根据统计编码代理id查询成员信息 */
  GET_Find_Sub_Agency_With_Finance_RefId:
    "/facade/sale/preTrial/agency/findSubAgencyWithFinanceRefId",
  /** 获取所有门市列表 */
  GET_All_Stores: "/console/store/getAllStore",
  /** 获取报表门市列表 */
  GET_Store_Stores: "/console/store/queryByUserId",
  /** 包车票后台退票预校验 */
  POST_REFUND_PRE_VERIFY: "/facade/sale/chartered/backend/refund/pre/verify",
  /** 包车票后台退票确认 */
  POST_REFUND_CONFIRM: "/facade/sale/chartered/backend/refund/confirm",
  /** 获取打印门票 */
  GET_APITICKET: "/facade/sale/tour/product/api/ticket/info",
  /** 修改旅游产品订单供应商订单号 */
  POST_PRODUCT_ORDER_INFO_UPDATE: "/facade/sale/tour/product/order/info/update",
  /** 行李票后台退票预校验 */
  POST_BAGGAGE_REFUND_PRE_VERIFY:
    "/facade/sale/ticket/baggage/backend/refund/pre/verify",
  /** 行李票后台退票确认 */
  POST_BAGGAGE_REFUND_CONFIRM:
    "/facade/sale/ticket/baggage/backend/refund/confirm",
  /** 获取apk列表 */
  POST_APK_PAGE_LIST: "/facade/sale/file/apk/page/list",
  /** 上传文件 */
  POST_UPLOAD_FILE: "/facade/sale/file/uploadFile",
  /** 提交apk */
  POST_UPLOAD_APK: "/facade/sale/file/upload/apk",
  /** 删除apk */
  POST_DELETE_APK: "/facade/sale/file/apk/delete",
  /** 更新apk状态 */
  POST_UPDATE_APK_STATUS: "/facade/sale/file/apk/updateStatus",
  /** 获取取卡串码 */
  GET_PRODUCT_PRINT_CARD_NO: "/facade/sale/tour/product/print/card/no/query",
  /** 获取旅游产品类型接口 */
  GET_PRODUCT_PACK_ED_TYPE: "/console/tour/product/pack/ed/type/query",
  /** 获取旅游产品路线接口 */
  GET_PRODUCT_PACK_ED_ROUTE: "/console/tour/product/pack/ed/route/query",
  /** 获取币种 */
  POST_USER_SWITCHCURRENCY: "/facade/sale/user/switchCurrency",
  /** 补价票下单 */
  POST_PREMIUM_TICKET_ORDER: "/facade/sale/order/premium/take",
  /** 补价票单出 */
  POST_SINGLE_OUT_PREMIUM_TICKET_ORDER:
    "/facade/sale/order/single/out/premium/take",
  /** 上报打印日志 */
  POST_LOGFORFRONT: "/facade/sale/user/logForFront",
  /** 启用、禁用产品库存 */
  POST_PRODUCT_STOCK_ENABLE: "/console/tour/product/stock/enable",
  /** 启用、禁用产品库存票号 */
  POST_PRODUCT_STOCK_NO_ENABLE: "/console/tour/product/stock/no/enable",
  /** 作废产品库存 */
  POST_PRODUCT_STOCK_INVALID: "/console/tour/product/stock/invalid",
  /** 查询异常订单 */
  GET_ORDER_ABNORMAL_QUERY: "/console/order/abnormal/query",
  /** 取消异常订单 */
  POST_ORDER_ABNORMAL_QUERY: "/facade/sale/order/close",
  /**  */
  POST_TOUR_PRODUCT_ALL: "/console/tour/product/all",
  /** 支付回调 */
  POST_PAYMENT_CALLBACK: "/facade/sale/pay/boci/callback",
  /** 日志上报 */
  POST_LOG_FOR_FRONT: "/facade/sale/pay/boci/addBocPosOperateLog",
  /** 获取支付方式列表 */
  GET_PAYMENT_LIST: "/facade/sale/pay/order/payments",
  /** 获取汇率列表 */
  GET_EXCHANGE_RATE_LIST: "/facade/sale/exchange/rate/list",
  /** 新增周期库存 */
  POST_CREATE_CYCLEINVENTORY: "/console/line/tour/cycleInventory/create",
  /** 新增周期库存 */
  POST_UPDATE_CYCLEINVENTORY: "/console/line/tour/cycleInventory/update",
  /** 获取库存详情 */
  POST_CYCLEINVENTORY_DETAIL: "/console/line/tour/cycleInventory/detail",
  /** 查询可添加的产品列表 */
  POST_AVAILABLE_PRODUCTS:
    "/console/line/tour/cycleInventory/availableProducts",
  /** 启用、禁用产品库存 */
  POST_CYCLEINVENTORY_STATUS: "/console/line/tour/cycleInventory/status",
  /** 限制车票使用规则列表 */
  GET_RESTRICTED_TICKET_RULE_LIST: "/console/line/limit/rule/listPage",
  /** 限制车票使用详情 */
  GET_RESTRICTED_TICKET_RULE_DETAIL: "/console/line/limit/rule",
  /** 限制车票使用更改或新增 */
  POST_RESTRICTED_TICKET_RULE_EDIT: "/console/line/limit/rule/edit",
  /** 获取线路信息列表 */
  GET_LINE_INFO_LIST: "/console/route/short/list",
  /** 获取Open票价格规则（包含标准价和营销价） */
  GET_OPEN_PRICE_WITH_MARKET: "/facade/sale/open/price/queryWithMarket",
  /** Open票下单 */
  POST_OPEN_ORDER_TAKE: "/facade/sale/open/order/take",
  /** 门市下单 */
  POST_FACADE_SALE_ORDER_TAKE: "/facade/sale/order/take",
  /** 返程改为Open */
  POST_FACADE_SALE_LINE_SEAT_CHANGE_TO_OPEN:
    "/facade/sale/line/seat/changeToOpen",
  /** 清空购物车解除锁位 */
  POST_OPEN_ORDER_CLEAR: "/facade/sale/line/seat/unchecked/cancel/user/all",
  /** 操作日志列表查询 */
  GET_OPERATE_LOG_LIST: "/facade/sale/ticket/log/queryOperateLog",
  /** 高级订单详情 */
  GET_CONSOLE_ORDER_DETAIL: "/console/order/detail",
  /** 门市订单详情 */
  GET_FACADE_ORDER_DETAIL: "/facade/sale/order/detail",
  /** 查询营销价列表 */
  GET_PRICE_MARKET_LIST: "/console/priceMarket/queryPriceMarket",
  /** 作废现金券 */
  POST_CASH_COUPON_INVALID: "/facade/sale/order/cash/coupon/invalid",
  /** 获取旅游产品订单详情 */
  GET_TOUR_PRODUCT_ORDER_INFO: "/facade/sale/tour/product/order/info",
  /** 代理预审单详情导出明细 */
  EXPORT_AGENCY_PRETRIAL_DETAIL: "/forms/pretrial/detail",
  /** 获取代理价格政策 */
  GET_AGENCY_SALE_PRICE_POLICY: "/console/agency/sale/price/policy",
  /** 修改代理用户姓名 */
  POST_MODIFY_AGENCY_USER_NAME: "/console/user/agency/modifyName",
  /** 修改代理编号 */
  POST_MODIFY_AGENCY_CODE: "/console/user/agency/modifyAgencyCode",
  /** 查询代理用户详情 */
  GET_AGENCY_USER_BY_ID: "/console/user/agency/queryAgencyUserById",
  /** 修改用户密码 */
  POST_MODIFY_PASSWORD: "/console/user/modPassword",
  /** 修改代理用户信息 */
  POST_MODIFY_AGENCY_USER: "/console/user/agency/modifyAgencyUser",
  /** 启用或禁用用户 */
  GET_ENABLE_OR_DISABLE_USER: "/console/user/enableOrDisAble",
  /** 获取所有角色 */
  GET_ALL_ROLES: "/console/user/role/getAllRole",
  /** 代理编号匹配查询 */
  GET_AGENCY_MATCH_CODE: "/console/agency/matchCode",
  /** 获取班次票价列表 */
  GET_LINE_PRICES_FOR_DISPLAY: "/facade/sale/lines/getLinePricesForDisplay",
  /** 票码转票号 */
  GET_TAKE_TICKET_CODE_TO_TICKET_NO:
    "/facade/sale/order/takeTicketCodeToTicketNo",
  /** 批量查询车票 */
  GET_BATCH_OPERATE_QUERY: "/console/ticket/batch/operate/query",
  /** 批量操作车票 */
  POST_BATCH_OPERATE: "/console/ticket/batch/operate",
  /** 后台退票预校验 */
  POST_BACKEND_REFUND_PRE_QUERY: "/console/ticket/backend/refund/pre/query",
  /** 后台退票计算 */
  POST_BACKEND_REFUND_COMPUTE: "/console/ticket/backend/refund/compute",
  /** 退票打印预览 */
  GET_REFUND_PRINT_PREVIEW: "/facade/sale/ticket/refund/print/preview",
  /** 退票打印确认 */
  GET_REFUND_PRINT_CONFIRM: "/facade/sale/ticket/refund/print/confirm",
  /** 创建支付 */
  POST_CREATE_PAY: "/facade/sale/pay/order/create",
  /** 查询销售单位（门市/代理/机构） */
  GET_SALE_UNIT_QUERY: "/console/order/saleUnit/query",
  /** 包车票详情 */
  POST_CHARTERED_TICKET_DETAIL: "/facade/sale/chartered/detail",
  /** 修改包车票打印状态 */
  POST_CHARTERED_TICKET_PRINT_STATUS:
    "/facade/sale/chartered/updatePrintStatus",
  /** 获取打印预览数据 */
  GET_PRINT_PREVIEW_DATA: "/facade/sale/ticket/print/preview",
  /** 打印确认 */
  POST_PRINT_CONFIRM: "/facade/sale/ticket/print/confirm",
  /** 修改普通车票打印状态 */
  POST_COMMON_TICKET_PRINT_STATUS:
    "/facade/sale/ticket/changeTicketPrintStatus",

  // ==================== 数据分析模块 ====================
  /** 获取今日&近30日交易数据 */
  GET_RECENT_TRADES: "/forms/tp/range/trades",
  /** 获取渠道占比数据 */
  GET_CHANNEL_RATE: "/forms/tp/recent/trades/channel/rate",
  /** 获取销售数据统计 */
  GET_SALES_DATA: "/forms/tp/recent/trades/every/day",
  /** 获取供应商销售排名 */
  POST_SUPPLIER_RANK: "/forms/tp/trades/supplier/rank",
  /** 获取供应商产品排名 */
  POST_SUPPLIER_PRODUCT_RANK: "/forms/tp/trades/supplier/product/rank",
  /** 获取销售单位套数排名（前20，不区分币种） */
  POST_SALE_UNIT_PACK_NUM: "/forms/tp/trades/saleUnit/pack/num",

  /** 获取旅游产品成本价 */
  GET_TOUR_PRODUCT_COST_PRICE: "/console/tour/product/costPrice",
  /** 旅游产品批量打包-新增 */
  POST_TOUR_PRODUCT_PACK_BATCH_ADD: "/console/tour/product/pack/batch/add",
  /** 旅游产品批量打包-修改 */
  POST_TOUR_PRODUCT_PACK_BATCH_MODIFY:
    "/console/tour/product/pack/batch/modify",
  /** 旅游产品批量打包-详情 */
  GET_TOUR_PRODUCT_PACK_BATCH_DETAIL: "/console/tour/product/pack/batch/detail",
  /** 旅游产品批量打包-分页查询 */
  GET_TOUR_PRODUCT_PACK_BATCH_PAGE: "/console/tour/product/pack/batch/page",
  /** 旅游产品批量打包-修改状态 */
  POST_TOUR_PRODUCT_PACK_BATCH_STATUS:
    "/console/tour/product/pack/batch/status",
  /** 批量查询打包商品的成本价，key 为 productId */
  POST_TOUR_PRODUCT_PACK_COST_PRICE_BATCH_QUERY:
    "/console/tour/product/pack/product/cost/price/batch/query",
  // ==================== 车票查询模块 ====================
  /** 根据票号查询车票信息 */
  GET_TICKET_BY_TICKET_NO: "/facade/sale/ticket/by/ticketNo",

  // ==================== 系统工具模块 ====================
  /** 获取验票监控日志列表 */
  POST_CHECK_IN_MONITOR_LOG_LIST: "/console/scan/ticket/log/listPage",
  /** 获取粤港消息监控分页列表 */
  POST_YG_NOTIFY_LOG_PAGE: "/console/ygNotifyLog/page",
  /** 获取粤港消息监控通知日志详情 */
  GET_YG_NOTIFY_LOG_DETAIL: "/console/ygNotifyLog/detail",
  /** 手动重试粤港消息（仅KC->粤港） */
  POST_YG_NOTIFY_LOG_MANUAL_RETRY: "/console/ygNotifyLog/manualRetry",

  // ==================== 任务管理模块 ====================
  /** 获取任务列表 */
  POST_TASK_MANAGE_PAGE: "/forms/schedule/manage/page",
  /** 取消任务 */
  POST_TASK_CANCEL: "/forms/schedule/cancel",
  /** 恢复任务 */
  POST_TASK_RECOVER: "/forms/schedule/recover",
  /** 设置任务最高优先级 */
  POST_TASK_PIN_TO_TOP: "/forms/schedule/pinToTop",
  /** 删除任务 */
  POST_TASK_DELETE: "/forms/schedule/delete",
  // ==================== 结算管理模块 ====================
  /** 查询月结算列表 */
  POST_MONTH_SETTLE_QUERY_PAGE_LIST: "/console/monthSettle/queryPageList",
  /** 检查递延任务是否可执行 */
  GET_MONTH_SETTLE_DEFER_TASK_EXECUTE_LIMIT:
    "/console/monthSettle/defer/task/execute/limit",
  /** 执行递延任务 */
  POST_MONTH_SETTLE_DEFER_TASK_EXECUTE:
    "/console/monthSettle/defer/task/execute",
  // ==================== 限制座位预警模块 ====================
  /** 预警信息分页列表 */
  GET_WARNING_MESSAGE_PAGE_LIST: "/console/line/warning/message/pageList",
  /** 预警规则分页列表 */
  GET_WARNING_RULE_PAGE_LIST: "/console/line/warning/rule/pageList",
  /** 关闭预警信息 */
  POST_WARNING_MESSAGE_CLOSE: "/console/line/warning/message/close",
  /** 批量关闭预警信息 */
  POST_WARNING_MESSAGE_BATCH_CLOSE:
    "/console//line/warning/message/batch/close",
  /** 修改预警规则状态 */
  POST_WARNING_RULE_CHANGE_STATUS: "/console/line/warning/rule/changeStatus",
  /** 获取预警规则详情 */
  GET_WARNING_RULE_DETAIL: "/console/line/warning/rule/detail",
  /** 新增预警规则 */
  POST_WARNING_RULE_ADD: "/console/line/warning/rule/add",
  /** 更新预警规则 */
  POST_WARNING_RULE_UPDATE: "/console/line/warning/rule/update",
  /** 获取交易记录汇总 */
  POST_TRADE_RECORD_SUMMARY: "/facade/sale/pay/transaction/successAmount",
  /** 获取取位留位报表路线列表 */
  GET_SEAT_HOLD_LINE_LIST: "/console/route/short/list/withPermission",
  /** 获取所有关口站点 */
  GET_ALL_GATEWAY_STATION: "/console/line/station/getTransferStations",
  /** 获取现金券列表（规则现金券+打印现金券） */
  GET_CASH_COUPON_LIST: "/forms/api/cash/coupon/list",

  // ==================== 报表管理模块 ====================
  /** 获取万能销售报表表头列表 */
  GET_UNIVERSAL_SALE_REPORT_HEADER_LIST: "/forms/report/generic/sell/params",
  /** 获取万能取位报表表头列表 */
  GET_UNIVERSAL_HOLD_REPORT_HEADER_LIST:
    "/forms/report/generic/confirm/seat/params",
  // ==================== 票务管理模块 ====================
  /** 获取接驳规则列表 */
  GET_TRANSFER_RULE_LIST: "/console/line/connect/rule/page",
  /** 查询接驳规则详情 */
  GET_TRANSFER_RULE_DETAIL: "/console/line/connect/rule/detail",
  /** 新增接驳规则 */
  POST_TRANSFER_RULE_ADD: "/console/line/connect/rule/add",
  /** 修改接驳规则 */
  POST_TRANSFER_RULE_UPDATE: "/console/line/connect/rule/update",
  /** 获取接驳班次列表 */
  GET_TRANSFER_LINE_LIST: "/console/line/connect/rule/line",
  /** 获取路线列表 */
  GET_ROUTE_LIST: "/console/route/page/list ",
  /** 获取行程列表分页版本 */
  GET_TRIP_LIST_PAGE: "/console/line/itinerary/listItineraryPage",
  /** 获取行程列表 */
  GET_TRIP_LIST: "/console/line/itinerary/listItinerary",

  // ==================== 站点管理模块 ====================
  /** 新增站点 */
  POST_STATION_ADD: "/console/line/station/add",
  /** 更新站点 */
  POST_STATION_UPDATE: "/console/line/station/update",
  /** 获取站点详情 */
  GET_STATION_INFO: "/console/line/station/info",
  /** 根据城市获取区域列表（往返站点用） */
  GET_AREA_SELECT: "/console/line/area/select",

  // ==================== 旅游产品模块 ====================
  /** 获取NCNB城市列表 */
  GET_NCNB_CITY_LIST: "/console/line/tour/product/hotelInfo/api/cities",
  /** 获取 NCNB 通过城市查询相关酒店列表 */
  GET_HOTEL_LIST_BY_CITY: "/console/line/tour/product/hotelInfo/api/hotelList",
  /** 按城市从 NCNB API 导入酒店静态信息 */
  POST_IMPORT_NCNB_HOTEL: "/console/line/tour/product/hotelInfo/api/import",
  /** 获取已绑定的酒店城市列表 */
  GET_HOTEL_CITY_LIST:
    "/console/line/tour/product/hotelInfo/api/getBindApiHotelCities",
  /** 通过城市id查询酒店列表 */
  GET_HOTEL_LIST_BY_CITY_ID:
    "/console/line/tour/product/hotelInfo/api/getBindHotelListByApiCityId",
  /** 按酒店信息ID从 NCNB API 导入房型静态信息 */
  POST_IMPORT_NCNB_ROOM: "/console/line/tour/product/hotel/api/import",
  /** 查询酒店产品早餐类型列表 */
  GET_HOTEL_BREAKFAST_TYPE_LIST:
    "/console/line/tour/product/hotel/api/queryTodayBreakfast",
  /** 查询酒店相关房型组合 */
  GET_HOTEL_ROOM_COMBINATION: "/console/line/tour/product/hotel/api/roomList",
  /** 导入酒店房型组合 */
  POST_IMPORT_HOTEL_ROOM_COMBINATION:
    "/console/line/tour/product/hotel/api/importSelectedRooms",
  /** 查询今日旅游产品订单统计 */
  GET_TODAY_TOUR_PRODUCT_ORDER_STATISTICS:
    "/facade/sale/tour/product/order/today/stat",
  /** API套票路线列表（按 packTypeCode 筛选） */
  GET_PACK_ROUTE_SHORT_LIST: "/facade/sale/tour/product/pack/route/short/list",
  /** API套票产品列表（按 packTypeCode 筛选） */
  GET_PACK_PRODUCTS: "/facade/sale/tour/product/pack/products/query",
  /** 西九文化区-查询子分类 */
  GET_WESTK_CATEGORY: "/facade/sale/tour/product/at/westk/category/query",
  /** 西九文化区-查询门票列表（单售） */
  GET_WESTK_PRODUCTS: "/facade/sale/tour/product/at/westk/products/query",
  /** 西九文化区-门票下单 */
  POST_WESTK_TAKE_ORDER: "/facade/sale/tour/product/at/westk/take/order",
  /** 西九文化区-关闭订单 */
  POST_WESTK_CLOSE_ORDER: "/facade/sale/tour/product/at/westk/close/order",
  /** 西九文化区-后台查询可导入产品 */
  GET_WESTK_IMPORT_PRODUCTS: "/console/tp/api/tickets/westk/product/query",
  /** 西九文化区-后台确认导入产品 */
  POST_WESTK_IMPORT_CONFIRM: "/console/tp/api/tickets/westk/product/confirm",
  /** 套票产品-关闭订单 */
  POST_PACK_CLOSE_ORDER: "/facade/sale/tour/product/pack/close/order",
} as const);

export default Api;
